import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Trash2, ChevronRight, X, Heart, Activity, Timer, MapPin, Dumbbell, Watch } from 'lucide-react';
import { workoutsApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate, cn } from '@/lib/utils';
import { computeWorkoutVolume, computeSetCount, formatVolume } from '@/lib/workout';
import type { Workout, WorkoutsListResponse, FilterWorkoutType } from '@/types';
import { FILTER_WORKOUT_TYPES } from '@/types';

export function WorkoutsListPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const urlTab = searchParams.get('type') as FilterWorkoutType | 'all' | null;
  const urlPage = searchParams.get('page');

  const activeTab: FilterWorkoutType | 'all' = urlTab && (urlTab === 'all' || FILTER_WORKOUT_TYPES.some(t => t.id === urlTab))
    ? urlTab
    : 'all';
  const page = urlPage ? parseInt(urlPage, 10) : 1;

  const handleTabChange = (tab: FilterWorkoutType | 'all') => {
    const newParams = new URLSearchParams();
    if (tab !== 'all') newParams.set('type', tab);
    setSearchParams(newParams);
  };

  const setPage = (newPage: number | ((prev: number) => number)) => {
    const resolvedPage = typeof newPage === 'function' ? newPage(page) : newPage;
    const newParams = new URLSearchParams(searchParams);
    if (resolvedPage > 1) {
      newParams.set('page', resolvedPage.toString());
    } else {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['workouts', page, activeTab],
    queryFn: () => workoutsApi.list({
      page,
      limit: 10,
      workoutType: activeTab === 'all' ? undefined : activeTab,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: workoutsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      setDeleteConfirm(null);
    },
  });

  const handleDeleteClick = (id: string, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      await deleteMutation.mutateAsync(deleteConfirm);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Virhe ladattaessa treenejä</p>
      </div>
    );
  }

  const response = data as WorkoutsListResponse | undefined;
  const workouts = response?.workouts || [];
  const total = response?.total || 0;
  const limit = response?.limit || 10;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/"
          className={cn(
            'p-2 rounded-lg text-gray-500',
            'transition-all touch-manipulation',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            'active:scale-95 active:bg-gray-200 dark:active:bg-gray-700'
          )}
          aria-label="Takaisin etusivulle"
        >
          <ArrowLeft className="h-6 w-6" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Kaikki treenit
          </h1>
          <p className="text-sm text-gray-500">
            {total} treeniä yhteensä
          </p>
        </div>
      </div>

      {/* Välilehdet */}
      <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max pb-2">
          <button
            onClick={() => handleTabChange('all')}
            className={cn(
              'px-4 py-2.5 rounded-full text-sm font-medium transition-all touch-manipulation',
              'active:scale-95',
              activeTab === 'all'
                ? 'bg-primary-600 text-white active:bg-primary-700'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600'
            )}
          >
            Kaikki
          </button>
          {FILTER_WORKOUT_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTabChange(type.id)}
              className={cn(
                'px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap touch-manipulation',
                'active:scale-95',
                activeTab === type.id
                  ? 'bg-primary-600 text-white active:bg-primary-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600'
              )}
            >
              {type.name}
            </button>
          ))}
        </div>
      </div>

      {/* Treenilista */}
      {workouts.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Dumbbell className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {activeTab === 'all' ? 'Ei vielä treenejä' : `Ei ${FILTER_WORKOUT_TYPES.find(t => t.id === activeTab)?.name.toLowerCase()}-treenejä`}
          </h3>
          <p className="text-gray-500">
            {activeTab === 'all'
              ? 'Aloita lisäämällä ensimmäinen treenisi!'
              : 'Kokeile toista välilehteä.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupByMonth(workouts).map((group) => (
            <div key={group.key} className="space-y-3">
              <h2 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-1">
                {group.label}
              </h2>
              {group.workouts.map((workout: Workout) => (
                <WorkoutCard
                  key={workout.id}
                  workout={workout}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Sivutus */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={cn(
              'min-h-[48px] px-5 font-medium rounded-xl',
              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
              'transition-all duration-200 touch-manipulation',
              'hover:bg-gray-200 dark:hover:bg-gray-700',
              'active:scale-[0.98] active:bg-gray-300 dark:active:bg-gray-600',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100'
            )}
          >
            Edellinen
          </button>
          <span className="text-gray-500 font-medium">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={cn(
              'min-h-[48px] px-5 font-medium rounded-xl',
              'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
              'transition-all duration-200 touch-manipulation',
              'hover:bg-gray-200 dark:hover:bg-gray-700',
              'active:scale-[0.98] active:bg-gray-300 dark:active:bg-gray-600',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100'
            )}
          >
            Seuraava
          </button>
        </div>
      )}

      {/* Poiston vahvistus -modaali */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Poista treeni?
              </h3>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={cn(
                  'p-2 text-gray-400 rounded-lg',
                  'transition-colors touch-manipulation',
                  'hover:text-gray-600 hover:bg-gray-100',
                  'active:bg-gray-200 dark:active:bg-gray-600'
                )}
                aria-label="Sulje"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Haluatko varmasti poistaa tämän treenin? Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={cn(
                  'flex-1 min-h-[48px] px-4 font-medium rounded-xl',
                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                  'transition-all duration-200 touch-manipulation',
                  'hover:bg-gray-200 dark:hover:bg-gray-600',
                  'active:scale-[0.98] active:bg-gray-300'
                )}
              >
                Peruuta
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className={cn(
                  'flex-1 min-h-[48px] px-4 font-medium rounded-xl',
                  'bg-red-600 text-white',
                  'transition-all duration-200 touch-manipulation',
                  'hover:bg-red-700',
                  'active:scale-[0.98] active:bg-red-800',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100'
                )}
              >
                {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Aktiviteettityypit
const CARDIO_ACTIVITY_TYPES = ['walking', 'running', 'cycling', 'swimming', 'hiking', 'elliptical', 'stair_climbing', 'rowing', 'snowboarding', 'resort_skiing_snowboarding'];
const STRENGTH_ACTIVITY_TYPES = ['strength', 'weight', 'gym', 'crossfit'];

function isCardioActivity(activityType: string): boolean {
  return CARDIO_ACTIVITY_TYPES.some(type => activityType.toLowerCase().includes(type));
}

function isStrengthActivity(activityType: string): boolean {
  return STRENGTH_ACTIVITY_TYPES.some(type => activityType.toLowerCase().includes(type));
}

function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0) {
    return `${hours} h ${mins} min`;
  }
  return `${mins} min`;
}

function capitalizeFirst(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Ryhmittele treenit kuukausiotsikoin (osio 7.1). Lista tulee API:sta
// laskevassa aikajärjestyksessä → uusin kuukausi ensin (Map säilyttää järjestyksen).
function groupByMonth(
  workouts: Workout[]
): { key: string; label: string; workouts: Workout[] }[] {
  const map = new Map<string, { label: string; workouts: Workout[] }>();
  for (const w of workouts) {
    const iso = w.startedAt || w.createdAt;
    const d = new Date(iso);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    let group = map.get(key);
    if (!group) {
      group = { label: capitalizeFirst(formatDate(iso, 'LLLL yyyy')), workouts: [] };
      map.set(key, group);
    }
    group.workouts.push(w);
  }
  return Array.from(map.entries()).map(([key, v]) => ({
    key,
    label: v.label,
    workouts: v.workouts,
  }));
}

function WorkoutCard({
  workout,
  onDelete,
}: {
  workout: Workout;
  onDelete: (id: string, e: React.MouseEvent | React.TouchEvent) => void;
}) {
  const workoutDate = workout.startedAt || workout.createdAt;
  const cardioActivity = workout.wearableActivities?.find(a => isCardioActivity(a.activityType));
  const strengthActivity = workout.wearableActivities?.find(a => isStrengthActivity(a.activityType));
  const hasManualSets = workout.exercises && workout.exercises.length > 0;
  const isCardioWorkout = !hasManualSets && cardioActivity;
  const isStrengthWorkout = !hasManualSets && strengthActivity;

  // Yhteenveto manuaalitreeneille (osio 7.1)
  const exercises = workout.exercises ?? [];
  const exerciseCount = exercises.length;
  const setCount = computeSetCount(exercises);
  const volume = computeWorkoutVolume(exercises);
  const summaryParts = [
    `${exerciseCount} ${exerciseCount === 1 ? 'liike' : 'liikettä'}`,
    `${setCount} ${setCount === 1 ? 'sarja' : 'sarjaa'}`,
  ];
  if (volume > 0) summaryParts.push(`${formatVolume(volume)} kg`);
  const summaryLine = summaryParts.join(' · ');
  const badgeNames = exercises
    .slice(0, 4)
    .map((we) => we.exercise?.name)
    .filter((n): n is string => !!n);
  const extraCount = exerciseCount > 4 ? exerciseCount - 4 : 0;

  const cardColorClass = isCardioWorkout
    ? 'border-l-4 border-l-primary-500'
    : isStrengthWorkout
      ? 'border-l-4 border-l-orange-500'
      : '';

  return (
    <Link
      to={`/workouts/${workout.id}`}
      className={cn(
        'card block',
        'transition-all duration-200 touch-manipulation',
        'hover:shadow-md active:scale-[0.99] active:bg-gray-50 dark:active:bg-gray-750',
        cardColorClass
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className={cn(
            'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
            isCardioWorkout
              ? 'bg-primary-100 dark:bg-primary-900/30'
              : isStrengthWorkout
                ? 'bg-orange-100 dark:bg-orange-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
          )}>
            {isCardioWorkout ? (
              <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            ) : (
              <Dumbbell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {workout.name || 'Treeni'}
            </h3>

            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatDate(workoutDate)}</span>
              {isCardioWorkout && cardioActivity?.durationMinutes && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <Timer className="h-3.5 w-3.5" />
                  <span>{formatDuration(cardioActivity.durationMinutes)}</span>
                </>
              )}
              {isCardioWorkout && cardioActivity?.distance && Number(cardioActivity.distance) > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{formatDistance(Number(cardioActivity.distance))}</span>
                </>
              )}
            </div>

            {hasManualSets && (
              <>
                <p className="text-sm text-gray-500 mt-1">{summaryLine}</p>
                {badgeNames.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {badgeNames.map((name, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                      >
                        {name}
                      </span>
                    ))}
                    {extraCount > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500">
                        +{extraCount}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}

            {workout.source === 'garmin' && (
              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                <Watch className="h-3 w-3" />
                Tuotu Garminista
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => onDelete(workout.id, e)}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(workout.id, e);
              }}
              className={cn(
                'p-3 rounded-lg text-gray-400',
                'transition-all duration-200 touch-manipulation',
                'hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
                'active:scale-95 active:text-red-600 active:bg-red-100'
              )}
              aria-label="Poista treeni"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <ChevronRight className="h-5 w-5 text-gray-300 dark:text-gray-600" />
          </div>
        </div>
      </div>
    </Link>
  );
}
